/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
// Use consistent styling
import 'sanitize.css/sanitize.css';

// Initialize i18n (language detection + bundled en/es resources) before render
import { languageReady } from 'locales/i18n';

// Import root app
import { App } from 'app';
import { ErrorBoundary } from 'app/components/ErrorBoundary';
import { installGlobalErrorHandlers } from 'utils/errorReporting';

import { HelmetProvider } from 'react-helmet-async';

import { configureAppStore } from 'store/configureStore';

import { SocketContextProvider } from 'utils/context/SocketContext';
import { HighlightedMessageProvider } from 'utils/context/HighlightedMessageContext';
import { BrowserRouter } from 'react-router-dom';
import { initializeAnalytics } from 'utils/googleAnalytics';
import { MobileNavigationProvider } from 'utils/context/NavigateContext';
import { FacebookProvider } from 'utils/context/FacebookContext';
import { NavigationProvider } from 'utils/context/NavigationContext';
import { ThemeProvider } from 'utils/context/ThemeProvider';
import { TooltipProvider } from '@heltarchat/ui';
import { CallProvider } from 'utils/context/CallContext';
import { SipCallProvider } from 'utils/context/SipCallContext';

// react-rnd's react-draggable reads `process.env.DRAGGABLE_DEBUG` at runtime;
// browsers have no global `process`, so provide a minimal stand-in.
(
  globalThis as unknown as {
    process?: { env: Record<string, string | undefined> };
  }
).process ??= { env: {} };

// Install global handlers BEFORE store + render so that any error during
// bootstrap is captured. Catches: unhandled Promise rejections, errors
// thrown in event handlers / async code that React's ErrorBoundary cannot
// see, and lazy-chunk load failures (auto-reloads once to refresh stale
// browser cache after a deploy).
installGlobalErrorHandlers();

const store = configureAppStore();
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

initializeAnalytics();

const app = (
  <ErrorBoundary>
    <Provider store={store}>
      <HelmetProvider>
        <React.StrictMode>
          <SocketContextProvider>
            <BrowserRouter
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <NavigationProvider>
                <HighlightedMessageProvider>
                  <MobileNavigationProvider>
                    <CallProvider>
                      <SipCallProvider>
                        <FacebookProvider>
                          <ThemeProvider defaultTheme='system'>
                            <TooltipProvider delayDuration={100}>
                              <App />
                            </TooltipProvider>
                          </ThemeProvider>
                        </FacebookProvider>
                      </SipCallProvider>
                    </CallProvider>
                  </MobileNavigationProvider>
                </HighlightedMessageProvider>
              </NavigationProvider>
            </BrowserRouter>
          </SocketContextProvider>
        </React.StrictMode>
      </HelmetProvider>
    </Provider>
  </ErrorBoundary>
);

// Immediate for English; any other language's file is fetched first, so the
// first screen is already in it.
void languageReady.then(() => root.render(app));
